import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGlobalPlayer } from '../../hooks/useGlobalPlayer';
import { detectEmbedType } from '../../pages/artist/components/EmbedPlayer';

function MiniEmbedPlayer({
  url,
  accentColor,
  isPlaying,
}: {
  url: string;
  accentColor: string;
  isPlaying: boolean;
}) {
  const embedType = detectEmbedType(url);

  if (embedType === 'youtube') {
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    const ytId = m ? m[1] : null;
    if (!ytId) return null;
    return (
      <iframe
        src={`https://www.youtube.com/embed/${ytId}?autoplay=${isPlaying ? 1 : 0}&rel=0&modestbranding=1`}
        className="w-full h-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        title="YouTube"
      />
    );
  }

  if (embedType === 'spotify') {
    const m = url.match(/open\.spotify\.com\/(track|album|playlist|episode|artist)\/([a-zA-Z0-9]+)/);
    if (!m) return null;
    return (
      <iframe
        src={`https://open.spotify.com/embed/${m[1]}/${m[2]}?theme=0`}
        className="w-full h-full border-0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        title="Spotify"
      />
    );
  }

  if (embedType === 'soundcloud') {
    const encodedUrl = encodeURIComponent(url);
    return (
      <iframe
        src={`https://w.soundcloud.com/player/?url=${encodedUrl}&color=${accentColor.replace('#', '')}&auto_play=${isPlaying}&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=false`}
        className="w-full h-full border-0"
        scrolling="no"
        allow="autoplay"
        title="SoundCloud"
      />
    );
  }

  if (embedType === 'niconico') {
    const nm = url.match(/(?:nicovideo\.jp\/watch|nico\.ms)\/(sm\d+|nm\d+|\d+)/);
    const nicoId = nm ? nm[1] : null;
    if (!nicoId) return null;
    return (
      <iframe
        src={`https://embed.nicovideo.jp/watch/${nicoId}?persistence=1&oldScript=1&referer=&from=0${isPlaying ? '&autoplay=1' : ''}`}
        className="w-full h-full border-0"
        allow="autoplay; fullscreen"
        title="niconico"
      />
    );
  }

  return null;
}

export function GlobalPlayer() {
  const { track, isPlaying, isVisible, play, pause, dismiss } = useGlobalPlayer();
  const [expanded, setExpanded] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setTimeout(() => setMounted(true), 10);
    } else {
      setMounted(false);
      setExpanded(false);
    }
  }, [isVisible]);

  if (!isVisible || !track) return null;

  const hasEmbed = track.embedType !== 'none' && track.embedType !== 'link';
  const artistPath =
    track.artistType === 'mock'
      ? `/artist/${track.artistId}`
      : `/artist/u/${track.artistId}`;

  const color = track.accentColor || '#00ff41';

  return (
    <>
      {/* ── Collapsed bar ── */}
      <div
        className={`fixed left-0 right-0 bottom-[60px] lg:bottom-[88px] z-[45] transition-all duration-500 ease-out ${
          mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        {/* Glow line */}
        <div
          className="h-px w-full"
          style={{
            background: `linear-gradient(to right, transparent, ${color}80, ${color}, ${color}80, transparent)`,
            boxShadow: `0 0 12px ${color}60`,
          }}
        />

        <div
          className="bg-black/95 backdrop-blur-xl border-t border-white/5"
          style={{ boxShadow: `0 -8px 40px ${color}15` }}
        >
          {/* Collapsed row */}
          <div className="flex items-center gap-3 px-4 py-2.5">
            {/* Waveform animation */}
            <div className="flex items-center gap-[2px] w-6 h-5 flex-shrink-0">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="flex-1 rounded-full"
                  style={{
                    background: color,
                    height: isPlaying ? `${40 + i * 15}%` : '20%',
                    transition: 'height 0.3s ease',
                    animation: isPlaying ? `wave-bar ${0.6 + i * 0.15}s ease-in-out infinite alternate` : 'none',
                    boxShadow: isPlaying ? `0 0 4px ${color}` : 'none',
                  }}
                />
              ))}
            </div>

            {/* Track info */}
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-bold tracking-wider truncate leading-tight">
                {track.trackName || 'NOW PLAYING'}
              </p>
              <p className="text-gray-500 text-[10px] tracking-widest truncate leading-tight mt-0.5">
                {track.artistName}
              </p>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Play / Pause — embed以外のみ */}
              {!hasEmbed && (
                <button
                  onClick={() => (isPlaying ? pause() : play())}
                  className="w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 cursor-pointer"
                  style={{
                    background: `${color}20`,
                    borderColor: `${color}60`,
                    boxShadow: isPlaying ? `0 0 12px ${color}40` : 'none',
                  }}
                >
                  <i
                    className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-sm`}
                    style={{ color }}
                  />
                </button>
              )}

              {/* Expand toggle (embed only) */}
              {hasEmbed && (
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 cursor-pointer"
                  style={{
                    background: `${color}20`,
                    borderColor: `${color}60`,
                    boxShadow: expanded ? `0 0 12px ${color}40` : 'none',
                  }}
                >
                  <i
                    className={`${expanded ? 'ri-arrow-down-s-line' : 'ri-arrow-up-s-line'} text-sm`}
                    style={{ color }}
                  />
                </button>
              )}

              {/* Artist link */}
              <Link
                to={artistPath}
                className="w-8 h-8 rounded-full flex items-center justify-center border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-user-line text-sm text-gray-400" />
              </Link>

              {/* Dismiss */}
              <button
                onClick={dismiss}
                className="w-8 h-8 rounded-full flex items-center justify-center border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300 cursor-pointer"
              >
                <i className="ri-close-line text-sm text-gray-500" />
              </button>
            </div>
          </div>

          {/* Expanded embed panel */}
          {hasEmbed && expanded && (
            <div
              className="px-4 pb-3 transition-all duration-300"
              style={{ borderTop: `1px solid ${color}20` }}
            >
              <div
                className="w-full rounded-xl overflow-hidden mt-2"
                style={{
                  height: track.embedType === 'youtube' || track.embedType === 'niconico' ? 180 : 152,
                  boxShadow: `0 0 20px ${color}20`,
                }}
              >
                <MiniEmbedPlayer
                  url={track.url}
                  accentColor={color}
                  isPlaying={isPlaying}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Wave animation keyframes */}
      <style>{`
        @keyframes wave-bar {
          0% { height: 20%; }
          100% { height: 90%; }
        }
      `}</style>
    </>
  );
}
